import { useEffect } from 'preact/compat'
import { getMetadataFromString } from '../app/hooks/useMetadata'
import { LOCALES } from '../app/constants/locales'
import { DataDog } from '../app/datadog'

const SUPPORTED_THEMES = [
  'tui',
  'tui-light',
  'ltur',
  'airtours',
  'crystalski',
  'firstchoice',
  'robinson',
  'tuimagiclife',
  'sundeals',
]

const SUPPORTED_PAYPAL_BUTTON_STYLES = ['light', 'dark']
const SUPPORTED_LOADER_IMAGES = [
  'BluePlane',
  'WhitePlane',
  'MinimalistBluePlane',
  'MinimalistWhitePlane',
]

export const isOverridden = value =>
  value !== undefined && value !== null

const isNonEmptyOverridden = value => isOverridden(value) && value !== ''

export const isValidUrlOrPath = value => {
  try {
    new URL(value, window.location.origin)
    return true
  } catch (e) {
    return false
  }
}

export const validateMfeAttributes = ({
  components,
  token,
  lang,
  theme,
  isPaymentBlocked,
  availablePaymentMethods,
  selectedPaymentMethod,
  stylesHref,
  paypalButtonStyle,
  paypalcreditButtonStyle,
  showHoursInTimer,
  showPaymentConfirmation,
  metadata,
  loaderImage,
  logAttributeError,
}) => {
  if (isOverridden(components)) {
    const parsedComponents = components.split(',').map(component => component.trim())
    const emptyComponents = parsedComponents.filter(component => !component)

    if (!parsedComponents.length || emptyComponents.length) {
      logAttributeError(
        'components',
        components,
        'Ensure it is a comma-separated list without empty entries.',
      )
    }
  }

  if (isOverridden(token) && !token.trim()) {
    logAttributeError('token', token, 'Token must be a non-empty string.')
  }

  if (isOverridden(lang)) {
    if (!LOCALES.includes(lang)) {
      logAttributeError('lang', lang, `Supported values: ${LOCALES.join(', ')}.`)
    }
  }

  if (isNonEmptyOverridden(theme) && !SUPPORTED_THEMES.includes(theme)) {
    logAttributeError(
      'theme',
      theme,
      `Supported values: ${SUPPORTED_THEMES.join(', ')}.`,
    )
  }

  const hasOverriddenAvailablePaymentMethods = isOverridden(availablePaymentMethods)
  const hasOverriddenSelectedPaymentMethod = isOverridden(selectedPaymentMethod)

  if (hasOverriddenAvailablePaymentMethods) {
    const parsedAvailablePaymentMethods = availablePaymentMethods
      .split(',')
      .map(method => method.trim())
    const emptyMethods = parsedAvailablePaymentMethods.filter(method => !method)

    if (!parsedAvailablePaymentMethods.length || emptyMethods.length) {
      logAttributeError(
        'available-payment-methods',
        availablePaymentMethods,
        'Ensure it is a comma-separated list without empty entries.',
      )
    }

    if (
      hasOverriddenSelectedPaymentMethod &&
      !parsedAvailablePaymentMethods.includes(selectedPaymentMethod)
    ) {
      logAttributeError(
        'selected-payment-method',
        selectedPaymentMethod,
        `It must be included in 'available-payment-methods' (${parsedAvailablePaymentMethods.join(', ')}).`,
      )
    }
  }

  if (isOverridden(stylesHref) && !isValidUrlOrPath(stylesHref)) {
    logAttributeError(
      'styles-href',
      stylesHref,
      'Provide a valid absolute or relative URL.',
    )
  }

  if (isNonEmptyOverridden(paypalButtonStyle)) {
    if (!SUPPORTED_PAYPAL_BUTTON_STYLES.includes(paypalButtonStyle)) {
      logAttributeError(
        'paypal-button-style',
        paypalButtonStyle,
        `Supported values: ${SUPPORTED_PAYPAL_BUTTON_STYLES.join(', ')}.`,
      )
    }
  }

  if (isNonEmptyOverridden(paypalcreditButtonStyle)) {
    if (!SUPPORTED_PAYPAL_BUTTON_STYLES.includes(paypalcreditButtonStyle)) {
      logAttributeError(
        'paypalcredit-button-style',
        paypalcreditButtonStyle,
        `Supported values: ${SUPPORTED_PAYPAL_BUTTON_STYLES.join(', ')}.`,
      )
    }
  }

  if (isOverridden(metadata)) {
    const parsedMetadata = getMetadataFromString(metadata)
    if (parsedMetadata === 'invalid') {
      logAttributeError(
        'metadata',
        metadata,
        'Provide a valid JSON object string.',
      )
    } else if (parsedMetadata === 'lengthError') {
      logAttributeError(
        'metadata',
        metadata,
        'Metadata length must not exceed 100 characters.',
      )
    }
  }

  if (isNonEmptyOverridden(loaderImage) && !SUPPORTED_LOADER_IMAGES.includes(loaderImage)) {
    logAttributeError(
      'loader-image',
      loaderImage,
      `Supported values: ${SUPPORTED_LOADER_IMAGES.join(', ')}.`,
    )
  }
}

export const useMfeAttributeValidation = attributes => {
  const logAttributeError = (attributeName, value, details) => {
    const message = `[MFE Payments]: Attribute '${attributeName}' has incorrect value. ${details}`

    console.error(message, value)
    DataDog.warn('MFE attribute validation error', {
      attribute: attributeName,
      value,
      details,
    })
  }

  useEffect(() => {
    validateMfeAttributes({
      ...attributes,
      logAttributeError,
    })
  }, [
    attributes.components,
    attributes.token,
    attributes.lang,
    attributes.theme,
    attributes.isPaymentBlocked,
    attributes.availablePaymentMethods,
    attributes.selectedPaymentMethod,
    attributes.stylesHref,
    attributes.paypalButtonStyle,
    attributes.paypalcreditButtonStyle,
    attributes.showHoursInTimer,
    attributes.showPaymentConfirmation,
    attributes.metadata,
    attributes.loaderImage,
  ])
}
